import { z } from 'zod';
import { CHANNEL_TYPES } from './channels.ts';

// Moving a project to another tenant (FR-12.11, docs/project-move.md) — what the staff app sends, and
// the plan and summary the database answers with (`plan_project_move`, `move_project`).

/** What happens to one of the project's channels (docs/project-move.md §5). */
export const projectMoveChoiceSchema = z.discriminatedUnion('choice', [
  /** The channel goes along: moved when nothing else uses it, copied otherwise. */
  z.object({ choice: z.literal('bring') }),
  /** The project uses a channel of the target tenant, of the same type, instead. */
  z.object({ choice: z.literal('replace'), with: z.uuid() }),
  /** The project stops using it. */
  z.object({ choice: z.literal('leave') }),
]);
export type ProjectMoveChoice = z.infer<typeof projectMoveChoiceSchema>;

/** A choice per channel, by the channel's id. */
export const projectMoveChoicesSchema = z.record(z.uuid(), projectMoveChoiceSchema).refine((choices) => Object.keys(choices).length <= 200, 'too many channels');
export type ProjectMoveChoices = z.infer<typeof projectMoveChoicesSchema>;

/** `POST …/move/preview` */
export const projectMovePreviewRequestSchema = z.object({
  to: z.uuid(),
  channels: projectMoveChoicesSchema,
});

/** `POST …/move` */
export const projectMoveRequestSchema = projectMovePreviewRequestSchema.extend({
  /** FR-12.7 — why, and who agreed: shown to both tenants once their audit log is (NFR-10). */
  reason: z.string().trim().min(1).max(1000),
  /** The digest of the plan staff previewed: the move refuses if it would now do something else. */
  digest: z.string().regex(/^[0-9a-f]{64}$/u),
  /** The target tenant's slug, typed: the one choice that choosing again cannot undo. */
  confirm: z.string().max(100),
});
export type ProjectMoveRequest = z.infer<typeof projectMoveRequestSchema>;

/** Why a move is refused (docs/project-move.md §6). */
export const PROJECT_MOVE_REFUSALS = [
  'project_missing',
  'target_missing',
  'same_workspace',
  'target_inactive',
  'source_unowned',
  'channel_choice_missing',
  'channel_type_off',
  'replacement_invalid',
  'replacement_duplicate',
  'delivery_in_flight',
  'plan_changed',
  'reason_missing',
  'confirm_mismatch',
  'busy',
] as const;
export type ProjectMoveRefusalCode = (typeof PROJECT_MOVE_REFUSALS)[number];

export const projectMoveRefusalSchema = z.object({ code: z.enum(PROJECT_MOVE_REFUSALS), channel: z.string().optional(), type: z.string().optional() });
export type ProjectMoveRefusal = z.infer<typeof projectMoveRefusalSchema>;

/** Shown, not refused. Unknown codes pass through: the panel shows their code. */
export const projectMoveWarningSchema = z.looseObject({ code: z.string() });

const tenantRef = z.object({ id: z.uuid(), slug: z.string(), name: z.string() });
const count = z.number().int().nonnegative();

export const projectMovePlanChannelSchema = z.object({
  id: z.uuid(),
  type: z.enum(CHANNEL_TYPES),
  name: z.string(),
  slug: z.string(),
  /** A retired channel holds history only and always comes along. */
  retired: z.boolean(),
  /** Nothing else of its tenant uses it: brought along, it moves rather than being copied. */
  exclusive: z.boolean(),
  bindings: count,
  deliveries: z.object({ sent: count, suppressed: count, dead: count, pending: count, sending: count }),
  choice: z.string().nullable(),
  outcome: z.enum(['moved', 'copied', 'replaced', 'left']).nullable(),
  /** The replacement, for `replaced`. */
  to: z.uuid().nullable(),
  /** Its slug in the target tenant, for a channel brought along. */
  slugInTarget: z.string().nullable(),
});
export type ProjectMovePlanChannel = z.infer<typeof projectMovePlanChannelSchema>;

export const projectMovePlanSchema = z.object({
  project: z.object({ id: z.uuid(), name: z.string(), slug: z.string(), slugInTarget: z.string() }),
  from: tenantRef,
  to: tenantRef.nullable(),
  counts: z.object({ forms: count, submissions: count, deliveries: count }),
  channels: z.array(projectMovePlanChannelSchema),
  /** The target tenant's channels, for a replacement. */
  targetChannels: z.array(z.object({ id: z.uuid(), type: z.enum(CHANNEL_TYPES), name: z.string() })),
  refusals: z.array(projectMoveRefusalSchema),
  warnings: z.array(projectMoveWarningSchema),
  digest: z.string(),
});
export type ProjectMovePlan = z.infer<typeof projectMovePlanSchema>;

export const projectMoveSummarySchema = z.object({
  project: z.uuid(),
  from: z.uuid(),
  to: z.uuid(),
  slug: z.object({ from: z.string(), to: z.string() }),
  channels: z.array(z.object({ channel: z.uuid(), outcome: z.enum(['moved', 'copied', 'replaced', 'left']), to: z.uuid().nullable(), retired: z.uuid().nullable() })),
  submissions: count,
  redriven: z.array(z.object({ id: z.uuid(), channelType: z.enum(CHANNEL_TYPES) })),
  leftUnsent: count,
});
export type ProjectMoveSummary = z.infer<typeof projectMoveSummarySchema>;
